import { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import TimelineRounded from "@mui/icons-material/TimelineRounded";
import StatusPill from "./StatusPill";
import { Transaction } from "../utils/types";
import { translate } from "../utils/currency";
import { buildTraceUrl } from "../utils/traceUrl";

const OPERATION_LABELS: Record<string, string> = {
  deposit: "Приём",
  cashin: "Пополнение",
  payout: "Выплата",
  withdrawal: "Вывод",
  transfer: "Перевод",
};

const formatMoney = (value: number | null | undefined, currency?: string) => {
  if (value === null || value === undefined) return "—";
  return `${value.toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}${currency ? ` ${currency.toUpperCase()}` : ""}`;
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
};

const formatRate = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : `${value}%`;

/**
 * Длительность обработки: от создания до последнего изменения. У незакрытых
 * операций updated_at пустой, и считать нечего.
 */
const formatDuration = (from: string, to: string | null) => {
  if (!to) return "—";
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} с`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} мин ${sec % 60} с`;
  return `${Math.floor(min / 60)} ч ${min % 60} мин`;
};

type RowProps = {
  label: string;
  value: ReactNode;
  copy?: string | null;
  mono?: boolean;
};

const Row = ({ label, value, copy, mono }: RowProps) => {
  const theme = useTheme();
  const empty = value === "—" || value === null || value === undefined;

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1.5}
      sx={{ py: 0.9 }}
    >
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: empty ? 400 : 600,
            fontFamily: mono ? "monospace" : undefined,
            wordBreak: "break-all",
            textAlign: "right",
            color: empty ? theme.palette.text.secondary : undefined,
          }}
        >
          {value}
        </Typography>
        {copy && (
          <Tooltip title="Скопировать">
            <IconButton
              size="small"
              sx={{ flexShrink: 0 }}
              onClick={() => navigator.clipboard.writeText(copy)}
            >
              <ContentCopyRounded sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="overline"
      sx={{ fontWeight: 700, letterSpacing: 0.6, opacity: 0.75 }}
    >
      {title}
    </Typography>
    <Divider sx={{ mb: 0.5 }} />
    {children}
  </Box>
);

type Props = {
  transaction: Transaction | null;
  merchantName?: string;
  onClose: () => void;
  /**
   * Перечитывает операцию с сервера. Нужна, когда живая лента не работает —
   * в режиме поиска она не подключена, и карточка иначе застыла бы на данных
   * момента открытия.
   */
  onRefresh?: (id: string) => Promise<boolean>;
};

const TransactionDetails = ({
  transaction,
  merchantName,
  onClose,
  onRefresh,
}: Props) => {
  const theme = useTheme();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);

  // Открыли другую операцию — прежняя отметка к ней не относится.
  useEffect(() => {
    setRefreshedAt(null);
    setRefreshFailed(false);
  }, [transaction?.id]);

  const handleRefresh = async () => {
    if (!transaction || !onRefresh) return;
    setRefreshing(true);
    setRefreshFailed(false);
    try {
      const ok = await onRefresh(transaction.id);
      if (ok) setRefreshedAt(new Date());
      else setRefreshFailed(true);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(transaction)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          p: 2.5,
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      {transaction && (
        <>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Box minWidth={0}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {OPERATION_LABELS[transaction.transaction_type] ??
                  transaction.transaction_type}{" "}
                · {formatMoney(transaction.start_amount, transaction.currency)}
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <StatusPill status={transaction.status} />
              </Box>
            </Box>
            <Stack direction="row" spacing={0.5} flexShrink={0}>
              {onRefresh && (
                <Tooltip title="Перечитать с сервера">
                  <span>
                    <IconButton
                      onClick={handleRefresh}
                      disabled={refreshing}
                      aria-label="Обновить данные операции"
                    >
                      {refreshing ? (
                        <CircularProgress size={20} />
                      ) : (
                        <RefreshRounded />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={onClose} aria-label="Закрыть">
                <CloseRounded />
              </IconButton>
            </Stack>
          </Stack>

          {(refreshedAt || refreshFailed) && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1.5,
                color: refreshFailed
                  ? theme.palette.semanticText.error
                  : theme.palette.text.secondary,
              }}
            >
              {refreshFailed
                ? "Не удалось обновить — операция не найдена или запрос не прошёл"
                : `Обновлено в ${refreshedAt?.toLocaleTimeString("ru-RU")}`}
            </Typography>
          )}

          <Button
            fullWidth
            variant="outlined"
            component={Link}
            href={buildTraceUrl(transaction.id)}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<TimelineRounded />}
            endIcon={<OpenInNewRounded sx={{ fontSize: 16 }} />}
            sx={{ mb: 2.5 }}
          >
            Трейс в Grafana
          </Button>

          <Section title="Идентификаторы">
            <Row
              label="ID транзакции"
              value={transaction.id}
              copy={transaction.id}
              mono
            />
            <Row
              label="Заказ мерчанта"
              value={transaction.merchant_order_id || "—"}
              copy={transaction.merchant_order_id}
              mono
            />
            <Row
              label="Заказ провайдера"
              value={transaction.mid_order_id || "—"}
              copy={transaction.mid_order_id}
              mono
            />
            <Row
              label="Мерчант"
              value={
                merchantName
                  ? `${merchantName} (#${transaction.merch_id})`
                  : `#${transaction.merch_id}`
              }
            />
            <Row
              label="Терминал провайдера"
              value={
                transaction.payment_terminal_id !== null
                  ? `#${transaction.payment_terminal_id}`
                  : "—"
              }
            />
            <Row label="Pago ID" value={transaction.pago_id || "—"} mono />
          </Section>

          <Section title="Суммы">
            <Row
              label="Заявлено"
              value={formatMoney(transaction.start_amount, transaction.currency)}
            />
            <Row
              label="Оплачено"
              value={formatMoney(transaction.paid_amount, transaction.currency)}
            />
            <Row label="Валюта" value={transaction.currency?.toUpperCase()} />
            <Row
              label="Курс обмена"
              value={transaction.exchange_rate ?? "—"}
            />
            <Row
              label="Курс провайдера"
              value={transaction.mid_exchange_rate ?? "—"}
            />
          </Section>

          <Section title="Комиссии и ставки">
            <Row label="Наша ставка" value={formatRate(transaction.our_rate)} />
            <Row
              label="Наша комиссия"
              value={formatMoney(transaction.our_fee, transaction.currency)}
            />
            <Row
              label="Ставка провайдера"
              value={formatRate(transaction.mid_rate)}
            />
            <Row
              label="Комиссия провайдера"
              value={formatMoney(transaction.mid_fee, transaction.currency)}
            />
          </Section>

          <Section title="Платёжные данные">
            <Row
              label="Метод"
              value={
                transaction.payment_method
                  ? translate(transaction.payment_method)
                  : "—"
              }
            />
            <Row label="Банк" value={transaction.bank_name || "—"} />
            <Row
              label="Реквизит"
              value={transaction.number || "—"}
              copy={transaction.number}
              mono
            />
            <Row label="Плательщик" value={transaction.fullname || "—"} />
            <Row
              label="Пользователь"
              value={transaction.user_id || "—"}
              copy={transaction.user_id}
              mono
            />
            <Row label="IP" value={transaction.user_ip || "—"} mono />
            <Row
              label="QR-код"
              value={
                transaction.qr_code ? (
                  <Link
                    href={transaction.qr_code}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Открыть
                  </Link>
                ) : (
                  "—"
                )
              }
            />
          </Section>

          <Section title="Время">
            <Row
              label="Создана"
              value={formatDateTime(transaction.created_at)}
            />
            <Row
              label="Обновлена"
              value={formatDateTime(transaction.updated_at)}
            />
            <Row
              label="Обработка"
              value={formatDuration(
                transaction.created_at,
                transaction.updated_at,
              )}
            />
          </Section>

          {transaction.mid_warn && (
            <Section title="Предупреждение провайдера">
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.semanticText.warning,
                  wordBreak: "break-word",
                }}
              >
                {transaction.mid_warn}
              </Typography>
            </Section>
          )}
        </>
      )}
    </Drawer>
  );
};

export default TransactionDetails;
